import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import API from '../../utils/constant';
import { storeRotatedSession } from '../../utils/Auth/rotatedSession';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { mustChangePassword, setMustChangePassword } from '../../utils/Auth/firstLogin';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import { Button, FormField } from '../../school_panel/components/ui';
import ThemeToggle from '../../components/Header/ThemeToggle';
import Logo from '../../images/logo/logo.png';
import SetupChecklist, { useSetupSummary } from './SetupChecklist';
import { BlueprintCard } from '../SchoolSetup/blueprint';

const MIN_LENGTH = 8;
const ADMIN_ROLES = ['school_admin', 'school', 'admin'];

function Steps({ current, steps }: { current: number; steps: string[] }) {
  return (
    <ol aria-label="Steps" className="mb-6 flex items-center gap-3">
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={label} aria-current={active ? 'step' : undefined} className="flex items-center gap-2">
            {i > 0 && <span aria-hidden className={`h-px w-6 ${done || active ? 'bg-accent' : 'bg-line-strong'}`} />}
            <span aria-hidden className={`grid h-6 w-6 flex-none place-items-center rounded-full font-mono text-xs font-semibold ${done ? 'bg-success text-white' : active ? 'bg-navy text-white' : 'bg-surface-2 text-muted'}`}>
              {done ? <Check size={14} /> : i + 1}
            </span>
            <span className={`text-[13px] ${active ? 'font-semibold text-ink' : 'text-muted'}`}>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

// First sign-in: replace the temporary password, then (school admins) set up the school.
export default function Welcome() {
  const { role, username, setToken } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = ADMIN_ROLES.includes(String(role));
  const [step, setStep] = useState(mustChangePassword() ? 0 : 1);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string; form?: string }>({});
  const [saving, setSaving] = useState(false);

  const summary = useSetupSummary(isAdmin && step === 1);
  const steps = isAdmin ? ['Set your password', 'Set up your school'] : ['Set your password'];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!current) errs.current = 'Enter the temporary password you were given.';
    if (next.length < MIN_LENGTH) errs.next = `Use at least ${MIN_LENGTH} characters.`;
    else if (next === current) errs.next = 'Choose a password different from the temporary one.';
    if (confirm !== next) errs.confirm = "The passwords don't match.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const { data } = await API.post('/api/school/me/password/', { current, new: next });
      // The change revokes every older token, so keep the fresh pair it returns (as Admin > Users does).
      if (!storeRotatedSession(data, setToken)) throw new Error('no tokens');
      setMustChangePassword(false);
      setCurrent(''); setNext(''); setConfirm('');
      if (isAdmin) {
        qc.invalidateQueries({ queryKey: ['school-setup-summary'] });
        setStep(1);
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      const d = err?.response?.data || {};
      setErrors({
        current: typeof d.current === 'string' ? d.current : undefined,
        next: typeof d.new === 'string' ? d.new : undefined,
        form: !d.current && !d.new ? (typeof d.detail === 'string' ? d.detail : 'Could not save your password. Try again.') : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-ground font-sans text-ink">
      <header className="flex items-center justify-between px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <img src={Logo} alt="" className="h-9 w-9 rounded-[10px]" />
          <span className="font-display text-base font-bold">YADA School</span>
        </div>
        <ThemeToggle />
      </header>
      <main className="mx-auto w-full max-w-[560px] px-4 pb-16 pt-4 sm:pt-10">
        <Steps current={step} steps={steps} />
        {step === 0 ? (
          <section aria-labelledby="pw-title" className="rounded-[20px] bg-surface p-6 shadow-card sm:p-8">
            <h1 id="pw-title" className="font-display text-[26px] font-extrabold tracking-[-0.02em]">Welcome{username ? `, ${username}` : ''}</h1>
            <p className="mt-1.5 text-sm text-muted">You signed in with a temporary password. Choose your own before you continue; only you will know it.</p>
            <form onSubmit={submit} noValidate className="mt-6 space-y-4">
              <FormField label="Temporary password" htmlFor="pw-current" error={errors.current}>
                <input id="pw-current" type="password" autoComplete="current-password" value={current}
                  onChange={(e) => setCurrent(e.target.value)} className={fieldClass} />
              </FormField>
              <FormField label="New password" htmlFor="pw-new" hint={`At least ${MIN_LENGTH} characters.`} error={errors.next}>
                <input id="pw-new" type="password" autoComplete="new-password" value={next}
                  onChange={(e) => setNext(e.target.value)} className={fieldClass} />
              </FormField>
              <FormField label="Confirm new password" htmlFor="pw-confirm" error={errors.confirm}>
                <input id="pw-confirm" type="password" autoComplete="new-password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className={fieldClass} />
              </FormField>
              {errors.form && <p role="alert" className="rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{errors.form}</p>}
              <Button type="submit" className="w-full" loading={saving}>Save password and continue</Button>
            </form>
            <p className="mt-4 text-center text-[13px] text-muted">
              Not you? <a href="/logout" className="font-semibold text-primary underline">Log out</a>
            </p>
          </section>
        ) : (
          <section aria-labelledby="setup-title" className="rounded-[20px] bg-surface p-6 shadow-card sm:p-8">
            <h1 id="setup-title" className="font-display text-[26px] font-extrabold tracking-[-0.02em]">Set up your school</h1>
            <p className="mb-5 mt-1.5 text-sm text-muted">Five steps get your school ready for teachers and students. Do them now or later; this list stays on your dashboard until it's done.</p>
            {summary.data?.grades === 0 && <div className="mb-5"><BlueprintCard compact /></div>}
            <SetupChecklist />
            <Button className="mt-5 w-full" variant="secondary" onClick={() => navigate('/dashboard', { replace: true })}>Go to the dashboard</Button>
          </section>
        )}
      </main>
    </div>
  );
}
